import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { latestArticles, hotComics } from '@/mocks/home';
import SEO from '@/components/base/SEO';

const articleContentMap: Record<string, {
  title: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  cover: string;
  content: string;
}> = {};

latestArticles.forEach((article) => {
  articleContentMap[article.slug] = {
    title: article.title,
    category: article.category,
    author: article.author,
    date: article.date,
    readTime: article.readTime,
    cover: article.cover,
    content: `
      <p>${article.excerpt}</p>
      <h2>作品背景与创作故事</h2>
      <p>${article.title}作为近年来备受瞩目的韩国漫画作品之一，其创作背景和故事设定深深吸引了大量读者。作者在创作过程中融入了丰富的文化元素和个人经历，使得作品不仅仅是简单的娱乐内容，更承载了深刻的情感和思想。</p>
      <p>韩国漫画产业近年来发展迅猛，尤其是在数字漫画（Webtoon）领域取得了举世瞩目的成就。从早期的纸质漫画到如今的全彩数字漫画，韩国漫画家们不断探索和创新，为全球读者带来了无数精彩的作品。</p>
      <h2>角色塑造与画风特点</h2>
      <p>本作的角色设计极具辨识度，每一个角色都有着鲜明的个性和独特的视觉风格。画风延续了韩国漫画一贯的精美细腻风格，尤其在表情和动作的刻画上极为出色，能够精准地传达角色的情感变化。</p>
      <p>韩国漫画的下拉式阅读格式（Webtoon）为创作者提供了更大的表现空间。长条形的画布让场景过渡更加流畅，也给读者带来了沉浸式的阅读体验。这种独特的阅读方式是韩漫区别于传统漫画的一大特色。</p>
      <h2>为什么推荐这部作品</h2>
      <p>首先，剧情紧凑且富有张力，每一话都能给读者带来新的惊喜。其次，角色的成长弧线清晰感人，读者能够与角色产生强烈的情感共鸣。最后，画风精美，色彩运用得当，整体视觉体验极佳。</p>
      <p>如果你正在寻找一部高质量的韩漫作品，这部绝对值得加入你的阅读清单。无论你是韩漫老读者还是刚刚入坑的新手，都能从中获得极佳的阅读体验。</p>
      <h2>读者评价与反馈</h2>
      <p>自上线以来，该作品收获了众多读者的好评。许多读者表示故事引人入胜、角色塑造立体、画风精美。在各大漫画平台上的评分一直维持在较高水平，是当之无愧的热门作品。</p>
      <p>如果你喜欢${article.category}类作品，这部漫画绝对不容错过！</p>
    `
  };
});

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = slug ? articleContentMap[slug] : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground-500 text-lg mb-4">文章未找到</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = latestArticles.filter((a) => a.category === article.category && a.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50 pt-20">
      <SEO
        title={`${article.title} - 韩漫世界`}
        description={`${article.title} - 深度解析${article.category}韩漫作品，阅读更多韩漫推荐文章，尽在韩漫世界，免费韩国漫画在线阅读平台。`}
        keywords={`${article.category}漫画,韩漫推荐,${article.title},免费漫画阅读`}
        canonicalUrl={`https://www.hanmanworld.com/article/${slug}`}
      />
      <article className="w-full px-4 md:px-6 max-w-4xl mx-auto py-10">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
          <span>/</span>
          <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">{article.category}漫画</Link>
          <span>/</span>
          <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
        </nav>

        <header className="mb-8">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
              {article.category}
            </span>
            <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
            <span className="text-xs text-foreground-400 whitespace-nowrap">&middot; {article.readTime} 阅读</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
            {article.title}
          </h1>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center">
              <i className="ri-user-line text-secondary-600 text-sm"></i>
            </div>
            <span className="text-sm text-foreground-600">{article.author}</span>
          </div>
        </header>

        <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
          <img
            src={article.cover}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div
          className="article-content text-foreground-700 mb-12 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="border-t border-background-200 pt-8">
          <div className="flex items-center gap-4 mb-4">
            <span className="text-sm text-foreground-600 font-medium whitespace-nowrap">分享文章：</span>
            <button className="w-9 h-9 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到微信">
              <i className="ri-wechat-line"></i>
            </button>
            <button className="w-9 h-9 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap" aria-label="分享到微博">
              <i className="ri-weibo-line"></i>
            </button>
            <button className="w-9 h-9 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap" aria-label="复制链接">
              <i className="ri-link"></i>
            </button>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 mb-1 line-clamp-2">
                      {item.title}
                    </h3>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{item.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}